import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE,
    TYPE_A, TYPE_B,
    PUBLISHED, UNPUBLISHED,
    PROVEN_NO, PROVEN_IN_USE, PRIOR_USE,
} from "const"
import { QidModel, QidModelCreate } from "server/models"
import {
    useInput,
    StateType,
} from "client/utils"
import {
    StoreType,
    QidModelValidation,
    SaveQidModel,
    closeModal,
    setQidModelEditModel,
    setQidModelReloadList,
    setQidModelReloadEditModel,
    setQidDatapointReloadList,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Feedback,
    RadioButton,
    CheckButton,
    CheckboxWrapper,
    Button,
    SpinnerIcon,
} from "client/components"

interface QidModelFormProps {
    model: QidModel
}

let isMounted: boolean

const Form = ({
    model,
}: QidModelFormProps) => {
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [productId] = useState(model.productId)

    const [name, setName] = useInput(model.name)
    const [type, setType] = useState(model.type)
    const [lowDemandMode, setLowDemandMode] = useState(model.lowDemandMode)
    const [highDemandMode, setHighDemandMode] = useState(model.highDemandMode)
    const [continuousMode, setContinuousMode] = useState(model.continuousMode)
    const [logicSolver, setLogicSolver] = useState(model.logicSolver)
    const [provenInUse, setProvenInUse] = useState(model.provenInUse)
    const [status, setStatus] = useState(model.status)

    const [validation, setValidation] = useState<QidModelValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return
        if (isMounted && modals.length) {
            dispatch(closeModal())
        }
    }

    const onSave = (saveMode: number) => {
        if (saveMode === 1) setSaveState(PENDING)
        if (saveMode === 2) setApplyState(PENDING)

        const newModelData: QidModelCreate = {
            productId,
            name,
            type,
            lowDemandMode,
            highDemandMode,
            continuousMode,
            logicSolver,
            provenInUse,
            status,
        }

        const updated = model && model.id && (
            model.name !== name
            || model.type !== type
            || model.lowDemandMode !== lowDemandMode
            || model.highDemandMode !== highDemandMode
            || model.continuousMode !== continuousMode
            || model.logicSolver !== logicSolver
            || model.provenInUse !== provenInUse
            || model.status !== status
        )

        SaveQidModel(
            model.id,
            newModelData,
            updated,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res.validation) return

            dispatch(setQidModelEditModel({
                id: res.newModelId || model.id,
                ...newModelData,
            }))

            dispatch(setQidModelReloadList(false))
            dispatch(setQidDatapointReloadList(false))
            setTimeout(() => {
                dispatch(setQidModelReloadList(true))
                dispatch(setQidDatapointReloadList(true))
            }, 0)

            if (saveMode === 1) onClose()
            if (saveMode === 2) {
                dispatch(setQidModelReloadEditModel(false))
                setTimeout(() => {
                    dispatch(setQidModelReloadEditModel(true))
                }, 0)
            }
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.mode}</Label>
                        <CheckboxWrapper>
                            <CheckButton
                                checked={lowDemandMode}
                                onChange={() => setLowDemandMode(!lowDemandMode)}
                                title={lang.modeLowDemand}
                            />
                            <CheckButton
                                checked={highDemandMode}
                                onChange={() => setHighDemandMode(!highDemandMode)}
                                title={lang.modeHighDemand}
                            />
                            <CheckButton
                                checked={continuousMode}
                                onChange={() => setContinuousMode(!continuousMode)}
                                title={lang.modeContinuous}
                            />
                        </CheckboxWrapper>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.type}</Label>
                        <FormGroup inline>
                            <RadioButton
                                checked={type === TYPE_A}
                                onChange={() => setType(TYPE_A)}
                                title={TYPE_A}
                            />
                            <RadioButton
                                checked={type === TYPE_B}
                                onChange={() => setType(TYPE_B)}
                                title={TYPE_B}
                            />
                        </FormGroup>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.logicSolver}</Label>
                        <FormGroup inline>
                            <RadioButton
                                checked={logicSolver}
                                onChange={() => setLogicSolver(true)}
                                title={lang.yes}
                            />
                            <RadioButton
                                checked={!logicSolver}
                                onChange={() => setLogicSolver(false)}
                                title={lang.no}
                            />
                        </FormGroup>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.provenReliabilityData}</Label>
                        <FormGroup inline>
                            <RadioButton
                                checked={provenInUse === PROVEN_NO}
                                onChange={() => setProvenInUse(PROVEN_NO)}
                                title={lang.no}
                            />
                            <RadioButton
                                checked={provenInUse === PROVEN_IN_USE}
                                onChange={() => setProvenInUse(PROVEN_IN_USE)}
                                title={lang.provenInUse}
                            />
                            <RadioButton
                                checked={provenInUse === PRIOR_USE}
                                onChange={() => setProvenInUse(PRIOR_USE)}
                                title={lang.priorUse}
                            />
                        </FormGroup>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.status}</Label>
                        <FormGroup inline>
                            <RadioButton
                                checked={status === PUBLISHED}
                                onChange={() => setStatus(PUBLISHED)}
                                title={lang.published}
                            />
                            <RadioButton
                                checked={status === UNPUBLISHED}
                                onChange={() => setStatus(UNPUBLISHED)}
                                title={lang.unpublished}
                            />
                        </FormGroup>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
